import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Build, DirectionsCar, Event } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import DownloadbleDataTable from '@/components/DownloadbleDataTable';
import { formatDate } from '@/helpers/date';
import { useMeta } from '@/hooks/meta';
import IDataTableColumn from '@/interfaces/IDataTableColumn';
import IVehicleMaintenanceStatusDTO from '@/interfaces/vehicles/IVehicleMaintenanceStatusDTO';
import maintenancesService from '@/services/maintenances';

function ViewButton({ vin }: { vin: string }) {
  const navigate = useNavigate();
  
  const handleClick = useCallback(() => {
    navigate(`/vehicles/inventory/${vin}`);
  }, [navigate, vin]);

  return (
    <Tooltip title="View Vehicle Details">
      <IconButton onClick={handleClick}>
        <DirectionsCar />
      </IconButton>
    </Tooltip>
  );
}

function ScheduleMaintenanceButton({ vin, subscriptionId }: { vin: string, subscriptionId?: string }) {
  const navigate = useNavigate();
  
  const handleClick = useCallback(() => {
    navigate('/maintenance/new', { state: { vehicleId: vin, subscriptionId: subscriptionId } });
  }, [navigate, vin]);

  return (
    <Tooltip title="Schedule Maintenance">
      <IconButton onClick={handleClick}>
        <Build />
      </IconButton>
    </Tooltip>
  );
}

function ViewMaintenanceButton({ id }: { id: string }) {
  const navigate = useNavigate();
  
  const handleClick = useCallback(() => {
    navigate(`/maintenance/list/${id}`);
  }, [navigate, id]);

  return (
    <Tooltip title="View Maintenance Details">
      <IconButton onClick={handleClick}>
        <Event />
      </IconButton>
    </Tooltip>
  );
}

export default function NeedMaintenance() {
  const [loading, setLoading] = useState(true);
  const [vehiclesWithStatus, setVehiclesWithStatus] = useState<IVehicleMaintenanceStatusDTO[]>([]);
  const [vehiclesCount, setVehiclesCount] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { setPageTitle } = useMeta();

  useEffect(() => {
    setPageTitle('Vehicles Needing Maintenance');
  }, [setPageTitle]);

  useEffect(() => {
    async function loadVehiclesNeedingMaintenance() {
      try {
        setLoading(true);
        const vehiclesData = await maintenancesService.listVehiclesNeedingMaintenance();
        setVehiclesWithStatus(vehiclesData.filter(vehicle => !!vehicle.subscriptionId));
        setVehiclesCount(vehiclesData.length);
      } catch (error) {
        enqueueSnackbar('Failed to load vehicles needing maintenance', {
          variant: 'error',
        });
      } finally {
        setLoading(false);
      }
    }

    loadVehiclesNeedingMaintenance();
  }, [enqueueSnackbar]);

  const addButton = useCallback(
    () => (
      <Box mt={1}>
        <Typography variant="body2">
          Vehicles count: {vehiclesCount}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          &ldquo;Days Since&rdquo; counts from the last logged maintenance or
          the start of the current subscription, whichever is more recent.
        </Typography>
      </Box>
    ),
    [vehiclesCount]
  );

  const columns = useMemo<IDataTableColumn[]>(
    () => [
      {
        name: 'vehicle.vin',
        label: 'VIN',
        options: {
          filter: true,
          sort: true,
          filterType: 'textField',
        },
      },
      {
        name: 'vehicle',
        label: 'Vehicle',
        options: {
          filter: true,
          filterType: 'textField',
          sort: true,
          customBodyRender: (vehicle) => 
            `${vehicle.make} ${vehicle.model} (${vehicle.year})`,
        },
      },
      {
        name: 'customer',
        label: 'Customer',
        options: {
          filter: true,
          filterType: 'textField',
          sort: true,
          customBodyRender: (customer) => 
            customer ? (
              <Button 
                size="small" 
                variant="text" 
                onClick={() => navigate(`/customers/list/${customer.id}`)}
              >
                {customer.name}
              </Button>
            ) : 'N/A',
        },
      },
      {
        name: 'lastMaintenance',
        label: 'Last Logged',
        options: {
          filter: false,
          sort: true,
          customBodyRender: (lastMaintenance) => 
            lastMaintenance ? formatDate(new Date(lastMaintenance.date)) : 'Never',
        },
      },
      {
        name: 'daysSinceLastMaintenance',
        label: 'Days Since Logged',
        options: {
          filter: true,
          sort: true,
          filterType: 'textField',
          customBodyRender: (days) => days ?? 'N/A',
        },
      },
      {
        name: 'effectiveDaysSinceMaintenance',
        label: 'Days Since',
        options: {
          filter: true,
          sort: true,
          filterType: 'textField',
          customBodyRender: (days) => days ?? 'N/A',
        },
      },
      {
        name: 'subscriptionStartDate',
        label: 'Sub. Started',
        options: {
          filter: false,
          sort: true,
          customBodyRender: (startDate) =>
            startDate ? formatDate(new Date(startDate)) : 'N/A',
        },
      },
      {
        name: 'nextMaintenance',
        label: 'Next Scheduled',
        options: {
          filter: false,
          sort: true,
          customBodyRender: (nextMaintenance) => {
            if (!nextMaintenance) {
              return <Chip label="Not Scheduled" color="warning" size="small" />;
            }
            
            const expectedDate = new Date(nextMaintenance.expectedDate);
            const isOverdue = expectedDate < new Date();
            
            return (
              <Box display="flex" alignItems="center">
                <Typography 
                  variant="body2" 
                  mr={1} 
                  color={isOverdue ? 'error' : 'inherit'}
                  fontWeight={isOverdue ? 'bold' : 'normal'}
                >
                  {formatDate(expectedDate)}
                </Typography>
                <ViewMaintenanceButton id={nextMaintenance.id} />
              </Box>
            );
          },
        },
      },
      {
        name: 'subscriptionId',
        label: 'Subscription ID',
        options: {
          filter: true,
          filterType: 'textField',
          sort: true,
          customBodyRender: (subscriptionId) => 
            subscriptionId ? (
              <Button 
                size="small" 
                variant="outlined" 
                onClick={() => navigate(`/subscriptions/list/${subscriptionId}`)}
              >
                {subscriptionId}
              </Button>
            ) : 'N/A',
        },
      },
      {
        name: '',
        label: 'Actions',
        options: {
          filter: false,
          sort: false,
          print: false,
          download: false,
          searchable: false,
          empty: true,
          viewColumns: false,
          customBodyRenderLite: (index: number) => (
            <Box display="flex">
              <ScheduleMaintenanceButton vin={vehiclesWithStatus[index].vehicle.vin} />
              <ViewButton vin={vehiclesWithStatus[index].vehicle.vin} />
            </Box>
          ),
        },
      },
    ],
    [navigate, vehiclesWithStatus]
  );

  return (
    <Box>
      <DownloadbleDataTable
        title="Vehicles Needing Maintenance"
        data={vehiclesWithStatus}
        columns={columns}
        options={{
          // Bumped because two columns were inserted mid-array: mui-datatables
          // restores a saved filterList by column index with no name check, so
          // reusing the old key re-applies stale filters to the wrong columns.
          storageKey: '@driveon:maintenance:need-maintenance:v2',
          rowsPerPage: 50,
          rowsPerPageOptions: [50, 100, 200],
          sortOrder: {
            name: 'effectiveDaysSinceMaintenance',
            direction: 'desc',
          },
          onTableChange: (action, tableState) => {
            if (action === 'filterChange' || action === 'propsUpdate') {
              setVehiclesCount(tableState.displayData.length);
            }
          },
          print: false,
          enableNestedDataAccess: '.',
          rowHover: true,
          selectableRows: 'none',
          customToolbar: addButton,
          downloadOptions: {
            filterOptions: {
              useDisplayedColumnsOnly: true,
              useDisplayedRowsOnly: true,
            },
          },
          textLabels: {
            body: {
              noMatch: loading ? (
                <CircularProgress />
              ) : (
                'No vehicles currently need maintenance'
              ),
            },
          },
        }}
      />
    </Box>
  );
} 